import React, { useCallback, useContext, useMemo } from "react";
import CoinsPieChart from "features/portfolio/common/CoinsPieChart";
import TwoChartsTableLayout from "common/Layouts/TwoChartsTableLayout";
import CoinsPricesChart from "features/portfolio/common/CoinsPricesChart";
import { selectPortfolioDataLoadingStates } from "@shared/slices/portfolio.slice";
import { CoinTableContent } from "features/portfolio/Assets/CoinTableContent";
import {
  ConsolidatedCoinData,
  GetPortfolioDataResponse,
  LanguageNameSpaces,
  PrecisionType,
  QueryStatus,
} from "@shared/types";
import MobileAssets from "features/portfolio/Assets/MobileAssets";
import usePortfolioData from "features/portfolio/common/usePortfolioData";
import { LAYOUT_DISTANCE_FROM_HEADER } from "features/portfolio/common/constants";
import { PrecisionConfig } from "types";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import styled, { useTheme } from "styled-components";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import AllocationCard from "features/portfolio/NewLook/AllocationCard.tsx";
import NewLookPortfolioLayout from "features/portfolio/NewLook/NewLookPortfolioLayout.tsx";
import {
  NewLookDrawerStack,
  NewLookMobileTop,
  NewLookPanelTable,
} from "features/portfolio/NewLook/styles.ts";
import { Panel } from "common/NewLook";
import useSearchInput from "utils/hooks/useSearchInput";
import Flex from "common/Boxes/Flex";
import BuyButton from "common/Buttons/BuyButton.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useIsSubUser, useSelectUser } from "@shared/utils/hooks/misc.ts";
import { PortfolioContext } from "features/portfolio/Portfolio.context.ts";
import PhoneAssets from "features/portfolio/NewLook/PhoneAssets.tsx";
import NewLookPortfolioChange from "features/portfolio/NewLook/NewLookPortfolioChange.tsx";

interface AssetsProps {
  dataSelector?: (state: any) => ConsolidatedCoinData[];
  loadingSelector?: (state: any) => { loading: boolean; loaded: boolean };
  precisionSelector?: (state: any) => PrecisionConfig | undefined;
  getPortfolioDataQuery?: QueryStatus<GetPortfolioDataResponse>;
}

export const Assets = ({
  dataSelector: propsDataSelector,
  loadingSelector: propsLoadingSelector,
  precisionSelector: propsPrecisionSelector,
  getPortfolioDataQuery,
}: AssetsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const filtersOnGrid = useMedia([`(min-width: 1400px)`], [true], false);
  const [SearchInput, searchValue] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    style: { width: 200, borderRadius: 8 },
  });
  const isSubUser = useIsSubUser();
  const user = useSelectUser();
  const { setBuyModalOpen } = useContext(PortfolioContext) || {};
  const { isNewLook } = useTheme();

  const {
    isDrawer,
    isBottomNavigator,
    getPortfolioDataLoading,
    onRefresh,
    clientToExchangeMapping,
    dataSelector,
    pieChartData,
    total,
    precisionSelector,
    loading,
    loaded,
  } = usePortfolioData({
    dataSelector: propsDataSelector,
    loadingSelector: propsLoadingSelector,
    precisionSelector: propsPrecisionSelector,
    getPortfolioDataQuery,
  });

  const coinsPieChart = useMemo(
    () => (
      <CoinsPieChart
        total={total}
        pieChartData={pieChartData}
        precisionSelector={precisionSelector}
        loadingSelector={selectPortfolioDataLoadingStates}
        tooltipElementId="coins-chart-tooltip"
        chartParentElementId="coins-chart"
        title={t("charts.assets_allocation")}
        hideTitle={isDrawer}
      />
    ),
    [total, pieChartData, t, isDrawer]
  );

  const coinsPricesChart = (
    <CoinsPricesChart
      key="pieChart"
      pieChartData={pieChartData}
      loaded={loaded}
    />
  );

  const mainContent = (
    <CoinTableContent
      clientToExchangeMapping={isDrawer ? undefined : clientToExchangeMapping}
      dataSelector={dataSelector}
      precisionSelector={precisionSelector}
      isLoading={loading}
      isLoaded={loaded}
      searchValue={isDrawer ? searchValue : undefined}
    />
  );

  /** On desktop pages the allocation is a half of the overview panel. */
  const inOverview = isNewLook && !isBottomNavigator && !isDrawer;

  const allocationCard = useMemo(
    () =>
      isNewLook ? (
        <AllocationCard
          title={inOverview ? undefined : t("charts.assets_allocation")}
          bare={inOverview}
          compact={isBottomNavigator}
          data={pieChartData}
          total={
            <TextWithPrecisionAndIcon
              precisionSelector={precisionSelector}
              precisionType={PrecisionType.AMOUNT}
              text={total}
              round={true}
            />
          }
          loading={loading && (!isBottomNavigator || !pieChartData.length)}
          chartId={isDrawer ? "coins-chart-drawer" : "coins-chart"}
        />
      ) : undefined,
    [
      isNewLook,
      t,
      pieChartData,
      precisionSelector,
      total,
      loading,
      isBottomNavigator,
      isDrawer,
      inOverview,
    ]
  );

  const renderTopContainer = useCallback(
    () =>
      !isNewLook ? (
        coinsPieChart
      ) : loading || pieChartData.length > 0 ? (
        <NewLookMobileTop>{allocationCard}</NewLookMobileTop>
      ) : (
        <></>
      ),
    [coinsPieChart, allocationCard, isNewLook, loading, pieChartData.length]
  );

  const getPageContent = () => {
    if (isNewLook && !isBottomNavigator && !isDrawer) {
      return (
        <NewLookPortfolioLayout
          chart={
            <NewLookPortfolioChange
              title={t("overview.assets_value")}
              pageName="Assets"
            />
          }
          allocation={allocationCard}
          allocationTitle={t("charts.assets_allocation")}
          showOverview={loading || pieChartData.length > 0}
          title={t("tabs.assets")}
          content={mainContent}
        />
      );
    }
    if (isBottomNavigator) {
      return (
        <div style={{ position: "relative", height: "100%" }}>
          {isNewLook && !isDrawer ? (
            <PhoneAssets
              dataSelector={dataSelector}
              precisionSelector={precisionSelector}
              loading={getPortfolioDataLoading}
              refreshing={loading}
              onRefresh={onRefresh}
              onBuyClick={
                !!setBuyModalOpen && !isSubUser && !user?.isWhopExperience()
                  ? () => setBuyModalOpen(true)
                  : undefined
              }
            />
          ) : (
            <MobileAssets
              renderTopContainer={renderTopContainer}
              precisionSelector={precisionSelector}
              loading={getPortfolioDataLoading}
              refreshing={loading}
              onRefresh={onRefresh}
              dataSelector={dataSelector}
            />
          )}
          {!!setBuyModalOpen && !isSubUser && !user?.isWhopExperience() && (
            <BuyButton
              onClick={() => setBuyModalOpen(true)}
              style={{ position: "absolute", bottom: 16 }}
            />
          )}
        </div>
      );
    }
    if (isDrawer) {
      return (
        <LoadingSpinnerOverlay
          isLoading={loading}
          spinnerSize="large"
          hideContentWhileLoading={true}
        >
          {isNewLook ? (
            <NewLookDrawerStack>
              {/* An account without coins: let the empty state lead. */}
              {(loading || pieChartData.length > 0) && allocationCard}
              <Panel
                title={t("tabs.assets")}
                actions={SearchInput}
                flush={true}
              >
                <NewLookPanelTable>{mainContent}</NewLookPanelTable>
              </Panel>
            </NewLookDrawerStack>
          ) : (
            <>
              <div style={{ position: "relative" }}>{coinsPieChart}</div>
              <Flex $justifyContent="flex-end" style={{ marginTop: 20 }}>
                {SearchInput}
              </Flex>
              <TableWrapper>{mainContent}</TableWrapper>
            </>
          )}
        </LoadingSpinnerOverlay>
      );
    }
    return (
      <TwoChartsTableLayout
        mobileCharts={[coinsPricesChart, mainContent]}
        smallChartUpperLeft={coinsPieChart}
        largeChartUpperMiddle={coinsPricesChart}
        mainContent={mainContent}
        useMobileCards={false}
        distanceFromHeader={LAYOUT_DISTANCE_FROM_HEADER}
        filtersOnGrid={filtersOnGrid}
      />
    );
  };

  return getPageContent();
};

export default Assets;

const TableWrapper = styled.div`
  margin-top: 20px;
`;
